import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
interface Option {
  id: string;
  label: string;
  icon?: React.ReactNode;
}
interface QuizQuestionProps {
  title: string;
  subtitle?: string;
  options: Option[];
  selectedIds: string[];
  onSelect: (id: string) => void;
  onContinue: () => void;
  multiSelect?: boolean;
  maxSelections?: number;
  note?: string;
  noteTitle?: string;
}
export function QuizQuestion({
  title,
  subtitle,
  options,
  selectedIds,
  onSelect,
  onContinue,
  multiSelect = false,
  maxSelections,
  note,
  noteTitle
}: QuizQuestionProps) {
  const handleSelect = (id: string) => {
    if (multiSelect) {
      if (selectedIds.includes(id)) {
        onSelect(id);
      } else if (!maxSelections || selectedIds.length < maxSelections) {
        onSelect(id);
      }
    } else {
      onSelect(id);
    }
  };
  const canContinue = selectedIds.length > 0;
  // Use smaller font for long titles (more than ~60 chars)
  const isLongTitle = title.length > 60;
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 8
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="flex flex-col h-full flex-1" data-id="element-77">
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.35
    }} className="mb-5" data-id="element-78">
        <h2 className={`${isLongTitle ? 'text-quiz-h2-long md:text-quiz-h2-long-desktop' : 'text-quiz-h2 md:text-quiz-h2-desktop'} font-heading font-semibold text-brand-dark mb-3 text-center`} data-id="element-79">
          {title}
        </h2>
        {subtitle && <p className="text-gray-500 text-quiz-option text-center" data-id="element-80">
            {subtitle}
          </p>}
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-3 mb-5 flex-1" data-id="element-81">
        {options.map(option => {
        const isSelected = selectedIds.includes(option.id);
        const isDisabled = multiSelect && maxSelections && !isSelected && selectedIds.length >= maxSelections;
        return <motion.button variants={itemVariants} whileTap={isDisabled ? {} : {
          scale: 0.98
        }} key={option.id} onClick={() => handleSelect(option.id)} disabled={isDisabled} className={`w-full text-left p-4 min-h-[56px] rounded-2xl border-2 transition-all duration-200 flex items-center ${isSelected ? 'border-brand-blue bg-blue-50 shadow-md scale-[1.01]' : isDisabled ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed' : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'}`} data-id="element-82">
              {option.icon && <div className={`mr-3 p-2.5 rounded-xl ${isSelected ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-83">
                  {option.icon}
                </div>}
              <span className={`flex-1 text-quiz-option md:text-quiz-option-desktop ${isSelected ? 'text-brand-blue font-bold' : 'text-gray-700 font-medium'}`} data-id="element-84">
                {option.label}
              </span>

              {/* Checkbox/Radio indicator */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ml-3 shrink-0 transition-colors ${isSelected ? 'border-brand-blue bg-brand-blue' : 'border-gray-300'} ${multiSelect ? 'rounded-md' : 'rounded-full'}`} data-id="element-85">
                {isSelected && <motion.svg initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-86">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-87" />
                  </motion.svg>}
              </div>
            </motion.button>;
      })}
      </motion.div>

      {note && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.3
    }} className="bg-brand-beige/40 p-4 rounded-2xl mb-4 border border-brand-beige/60" data-id="element-88">
          {noteTitle && <p className="font-heading font-bold text-brand-dark text-disclaimer mb-1.5 uppercase tracking-wider" data-id="element-89">
              {noteTitle}
            </p>}
          <p className="text-helper text-gray-700 leading-relaxed" data-id="element-90">{note}</p>
        </motion.div>}

      <div className="mt-auto pt-3 pb-safe" data-id="element-91">
        {multiSelect && maxSelections && <p className="text-center text-badge text-gray-500 mb-3 font-medium" data-id="element-92">
            {selectedIds.length} de {maxSelections} seleccionados
          </p>}
        <Button onClick={onContinue} disabled={!canContinue} data-id="element-93">
          Continuar
        </Button>
      </div>
    </div>;
}